import React from 'react';
import { 
  CloudRain, FileCheck, MapPin, 
  History, PenTool, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';

const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: CloudRain,
      title: "Weather Captured on Inspections",
      description: "When an online inspection starts, RunOffTrack captures current and recent precipitation from Open-Meteo for the project coordinates."
    },
    {
      icon: FileCheck,
      title: "Signed Inspection Reports",
      description: "Generate consistent PDF records with inspection findings, notes, photos, weather context, and the inspector's signature."
    },
    {
      icon: MapPin,
      title: "Multi-Site Dashboard",
      description: "Manage multiple construction projects from one view. See which sites have upcoming or overdue inspection dates at a glance."
    },
    {
      icon: PenTool,
      title: "Digital Signatures",
      description: "Sign and finalize inspection reports directly from a phone, tablet, or computer."
    },
    {
      icon: History,
      title: "Project History",
      description: "Keep inspections and completed reports organized by project so records are easier to retrieve."
    },
    {
      icon: ShieldCheck,
      title: "State Requirement Directory",
      description: "Use our state directory as a starting point for permit research, then configure and conduct inspections for your project.",
      link: "/swppp-requirements",
      linkText: "View State Directory"
    }
  ];

  return (
    <div className="page-shell">
      <SEO 
        title="RunOffTrack Features - SWPPP Inspection Software"
        description="Explore field inspection features including weather context, digital signatures, project history, and PDF reports."
        canonical="/features"
      />
      
      <div className="page-intro">
        <div className="container relative z-10 px-4 text-center">
          <Badge variant="outline" className="mb-8 py-2 px-4 text-sm border-primary/20 bg-primary/5 text-primary font-medium rounded-full">
            Built for the Field
          </Badge>
          <h1 className="mx-auto mb-8 max-w-4xl text-5xl font-black leading-[1.05] tracking-[-0.05em] text-foreground md:text-7xl">
            Clearer inspections. Better records.
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
            Eliminate the clipboard. Reduce the risk.
          </p>
        </div>
      </div>

      <div className="bg-muted/30 py-24 md:py-32">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-card border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg group flex flex-col">
                <CardContent className="p-10 flex-grow">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  {feature.link && (
                      <Link to={feature.link} className="inline-flex items-center text-sm font-medium text-primary hover:underline">
                          {feature.linkText} <ArrowRight size={14} className="ml-1"/>
                      </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <div className="py-24 md:py-32 bg-background text-center">
         <div className="container px-4">
            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-foreground">Ready to streamline your inspections?</h2>
            <div className="flex justify-center">
                <Button size="lg" className="text-lg h-16 px-12 rounded-full font-bold shadow-xl" asChild>
                  <a href={getSignupUrl()} onClick={() => trackSignupIntent('features_bottom_cta')}>Start Free <ArrowRight className="ml-2 h-5 w-5"/></a>
                </Button>
            </div>
         </div>
      </div>
    </div>
  );
};

export default FeaturesPage;
